import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"

import type { ExtendedResourceInvoice } from "@repo/supabase/database-types"
import { If } from "@repo/ui/components/ui/if"
import { pick } from "@repo/utils/collections"
import { logger } from "@repo/utils/logger"
import { dataTableBaseSearchSchema } from "~/lib/schemas/common"
import {
  type Pagination,
  ResourceInvoicesTable,
} from "~/modules/resources/components/resource-invoices-table"
import { getExtendedResourceInvoicesQueryOptions } from "~/modules/resources/resource-invoices.queries"
import { getExtendedResourceBySlugQueryOptions } from "~/modules/resources/resources.queries"

const log = logger.child({
  module: "resources-resource-invoices-route",
})

const searchSchema = dataTableBaseSearchSchema

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/resources/$resourceSlug/invoices",
)({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) =>
    pick(search, ["q", "page", "page_size", "sorting", "columnFilters"]),
  loader: async ({ context, params, deps }) => {
    // Fetch resource first to get its unit_id
    const resource = await context.queryClient
      .ensureQueryData(
        getExtendedResourceBySlugQueryOptions({ slug: params.resourceSlug }),
      )
      .catch((error) => {
        log.error("Error getting extended resource by slug", { error })
        return null
      })

    // Use the resource's unit_id to fetch invoices (non-blocking)
    if (resource) {
      context.queryClient
        .ensureQueryData(
          getExtendedResourceInvoicesQueryOptions({
            resourceUnitId: resource.unit_id,
            filters: deps,
          }),
        )
        .catch((error) => {
          log.error("Error getting extended resource invoices", { error })
          return null
        })
    }

    return {
      filters: deps,
    }
  },
})

function RouteComponent() {
  const params = Route.useParams()
  const { filters } = Route.useLoaderData()
  const navigate = useNavigate()
  const { data: resource } = useSuspenseQuery(
    getExtendedResourceBySlugQueryOptions({ slug: params.resourceSlug }),
  )

  const { data: invoicesData } = useQuery(
    getExtendedResourceInvoicesQueryOptions({
      resourceUnitId: resource.unit_id,
      filters,
    }),
  )

  const handleViewInvoice = (invoice: ExtendedResourceInvoice) => {
    navigate({
      to: "/resource-invoices/$invoiceId",
      params: { invoiceId: invoice.id },
    })
  }

  const pagination: Pagination = {
    total: invoicesData?.count ?? 0,
    page: filters.page,
    page_size: filters.page_size,
  }

  return (
    <If
      condition={invoicesData?.invoices}
      fallback={
        <div className="h-full flex-1 overflow-auto">
          <div className="flex h-64 items-center justify-center">
            <div className="text-white/70">Loading invoices...</div>
          </div>
        </div>
      }
    >
      {(data) => (
        <ResourceInvoicesTable
          invoices={data}
          pagination={pagination}
          enableStatusActions
          onViewInvoice={handleViewInvoice}
          onSelectInvoice={handleViewInvoice}
          initialColumnFilters={filters.columnFilters ?? []}
          initialSorting={filters.sorting ?? []}
        />
      )}
    </If>
  )
}
